/** Totaux PDF document — aligné approve-vente.calculateTotalsFromDocument. */

export type DocumentPdfPriceContext = {
    isAchat: boolean;
    isStockDoc: boolean;
};

function parseNum(raw: unknown): number {
    const n = parseFloat(String(raw ?? ''));
    return Number.isFinite(n) ? n : 0;
}

/** Prix unitaire ligne (valeur unitaire stock, prix achat, prix vente…). */
function deriveUnitPriceFromLineTotal(ligne: any): number {
    const qnt = parseNum(ligne?.qnt);
    if (qnt <= 0) {
        return 0;
    }
    for (const key of ['total_ht', 'result_ht']) {
        const lineHt = parseNum(ligne?.[key]);
        if (lineHt > 0) {
            return lineHt / qnt;
        }
    }
    return 0;
}

export function getPrixUnitaireLigne(ligne: any, ctx: DocumentPdfPriceContext): number {
    if (ctx.isStockDoc) {
        // Aligné LogicielCaisseTransfertDetailComponent (prix_vente catalogue + valeur ligne).
        return (
            parseNum(ligne?.valeur_unitaire) ||
            parseNum(ligne?.prix_vente) ||
            parseNum(ligne?.prix_ht) ||
            parseNum(ligne?.result_ht) ||
            deriveUnitPriceFromLineTotal(ligne) ||
            0
        );
    }
    if (ctx.isAchat) {
        return (
            parseNum(ligne?.prix_achat) ||
            parseNum(ligne?.prixAchat) ||
            parseNum(ligne?.result_ht) ||
            parseNum(ligne?.prix_ht) ||
            0
        );
    }
    // Aligné approve-vente (valeur unitaire = prix_ht en vente, ex. bon de sortie).
    return (
        parseNum(ligne?.prix_ht) ||
        parseNum(ligne?.prix_vente) ||
        parseNum(ligne?.result_ht) ||
        0
    );
}

/** Total HT net ligne (total_ht API ou qnt × PU − remise). */
export function getLigneTotalHtNet(ligne: any, unitPrice: number): number {
    if (ligne?.total_ht != null && ligne?.total_ht !== '') {
        const fromApi = parseNum(ligne.total_ht);
        if (Number.isFinite(fromApi)) {
            return fromApi;
        }
    }
    const q = parseNum(ligne?.qnt);
    const remise = parseNum(ligne?.remise);
    const htBrut = q * unitPrice;
    const remiseAmt = remise > 0 ? htBrut * (remise / 100) : 0;
    return htBrut - remiseAmt;
}

export interface DocumentPdfTotalsResult {
    totalHT: number;
    totalTVA: number;
    totalTTC: number;
    timbreFiscal: number;
    netAPayer: number;
    tvaSummary: { rate: string; base: number; amount: number }[];
}

function parseDocNum(val: unknown): number {
    if (val == null || val === '') {
        return NaN;
    }
    const n =
        typeof val === 'number'
            ? val
            : parseFloat(String(val).replace(/[^\d.,-]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
}

function fromDoc(doc: any, key: string): number {
    return parseDocNum(doc?.[key]);
}

/** Normalise un libellé de type document (ERP / LC). */
export function normalizeDocumentTypeTitreRaw(raw: unknown): string {
    return String(raw ?? '')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

export function normalizePdfDocumentTypeTitre(doc: any): string {
    return normalizeDocumentTypeTitreRaw(doc?.typeDocument?.titre || doc?.type_document?.titre || '');
}

export function pdfIsAchatDocumentType(typeTitre: string): boolean {
    return ['COMMANDE FOURNISSEUR', 'BON RECEPTION', 'BON DE RECEPTION', 'FACTURE FOURNISSEUR'].includes(
        typeTitre
    );
}

/**
 * Documents dont le détail suit la même logique que le transfert entrepôt LC
 * (stock suggestions, valeur unitaire catalogue, matrice variantes…).
 */
export function pdfIsStockLcDocumentType(typeTitre: string): boolean {
    const raw = normalizeDocumentTypeTitreRaw(typeTitre);
    if (!raw) {
        return false;
    }
    if (
        [
            'INVENTAIRE',
            'BON TRANSFERT',
            'TRANSFERT',
            'BON DE LIVRAISON',
            'BON RECEPTION',
            'BON DE RECEPTION',
            'BON DE SORTIE',
        ].includes(raw)
    ) {
        return true;
    }
    return raw.includes('PV') && raw.includes('ECART');
}

export function pdfIsBonDeSortieDocumentType(typeTitre: string): boolean {
    return normalizeDocumentTypeTitreRaw(typeTitre) === 'BON DE SORTIE';
}

export function pdfIsBonReceptionDocumentType(typeTitre: string): boolean {
    const raw = normalizeDocumentTypeTitreRaw(typeTitre);
    return raw === 'BON RECEPTION' || raw === 'BON DE RECEPTION';
}

/** Quantité affichée depuis getStockGlobaleByErpProducts (LC). */
export function pickLcGlobaleStockQtyForDocumentType(
    typeTitre: string,
    pair: { physique?: number; disponible?: number } | undefined
): number {
    if (!pair) {
        return 0;
    }
    const raw = normalizeDocumentTypeTitreRaw(typeTitre);
    if (raw === 'BON DE SORTIE') {
        return Math.trunc(Number(pair.physique ?? 0) || 0);
    }
    if (raw === 'BON TRANSFERT' || raw === 'TRANSFERT' || raw === 'INVENTAIRE') {
        return Math.trunc(Number(pair.disponible ?? 0) || 0);
    }
    if (raw === 'BON DE LIVRAISON' || raw === 'BON RECEPTION' || raw === 'BON DE RECEPTION') {
        return Math.trunc(Number(pair.disponible ?? 0) || 0);
    }
    return Math.trunc(Number(pair.physique ?? pair.disponible ?? 0) || 0);
}

/** Filtrer le stock LC par entrepôt document (comme transfert entrepôt). */
export function pdfShouldFilterStockByEntrepotType(typeTitre: string): boolean {
    return pdfIsStockLcDocumentType(typeTitre);
}

export function buildDocumentPdfPriceContext(doc: any): DocumentPdfPriceContext {
    const type = normalizePdfDocumentTypeTitre(doc);
    return {
        isAchat: pdfIsAchatDocumentType(type),
        isStockDoc: pdfIsStockLcDocumentType(type),
    };
}

export function calculateDocumentPdfTotals(
    doc: any,
    lignesInput: any[],
    priceCtx: DocumentPdfPriceContext
): DocumentPdfTotalsResult {
    const lignes = Array.isArray(lignesInput) ? lignesInput : [];

    let timbreFiscal = fromDoc(doc, 'timbre_fiscal');
    if (!Number.isFinite(timbreFiscal)) {
        timbreFiscal = 0.6;
    }

    const sumLinesHt = () =>
        lignes.reduce((sum, l) => {
            const pu = getPrixUnitaireLigne(l, priceCtx);
            return sum + getLigneTotalHtNet(l, pu);
        }, 0);

    const sumLinesTva = () =>
        lignes.reduce((sum, l) => {
            const pu = getPrixUnitaireLigne(l, priceCtx);
            const base = getLigneTotalHtNet(l, pu);
            const tva = parseFloat(String(l?.tva ?? 0)) || 0;
            return sum + (base * tva) / 100;
        }, 0);

    if (!lignes.length) {
        const totalHT = fromDoc(doc, 'total_ht') || 0;
        const totalTVA = fromDoc(doc, 'total_tva') || 0;
        const totalTTC = fromDoc(doc, 'total_ttc') || totalHT + totalTVA;
        const netAPayer = fromDoc(doc, 'net_payer') || totalTTC + timbreFiscal;
        return {
            totalHT,
            totalTVA,
            totalTTC,
            timbreFiscal,
            netAPayer,
            tvaSummary: [],
        };
    }

    const docType = normalizePdfDocumentTypeTitre(doc);
    const headerUsable = (key: string): boolean => {
        if (pdfIsStockLcDocumentType(docType)) {
            const n = fromDoc(doc, key);
            return Number.isFinite(n) && n > 0;
        }
        const raw = (doc as any)?.[key];
        return raw != null && raw !== '';
    };

    const totalHT = headerUsable('total_ht') ? fromDoc(doc, 'total_ht') : sumLinesHt();
    const totalTVA = headerUsable('total_tva') ? fromDoc(doc, 'total_tva') : sumLinesTva();
    const totalTTC = headerUsable('total_ttc') ? fromDoc(doc, 'total_ttc') : totalHT + totalTVA;
    const netAPayer = headerUsable('net_payer') ? fromDoc(doc, 'net_payer') : totalTTC + timbreFiscal;

    const tvaMap = new Map<string, { base: number; amount: number }>();
    lignes.forEach((l) => {
        const tva = parseFloat(String(l?.tva ?? 0)) || 0;
        const rate = tva.toString();
        const pu = getPrixUnitaireLigne(l, priceCtx);
        const base = getLigneTotalHtNet(l, pu);
        const amount = (base * tva) / 100;
        if (tvaMap.has(rate)) {
            const prev = tvaMap.get(rate)!;
            tvaMap.set(rate, { base: prev.base + base, amount: prev.amount + amount });
        } else {
            tvaMap.set(rate, { base, amount });
        }
    });

    const tvaSummary = Array.from(tvaMap.entries()).map(([rate, { base, amount }]) => ({
        rate,
        base: parseFloat(base.toFixed(3)),
        amount: parseFloat(amount.toFixed(3)),
    }));

    return {
        totalHT: Number.isFinite(totalHT) ? totalHT : 0,
        totalTVA: Number.isFinite(totalTVA) ? totalTVA : 0,
        totalTTC: Number.isFinite(totalTTC) ? totalTTC : 0,
        timbreFiscal: Number.isFinite(timbreFiscal) ? timbreFiscal : 0,
        netAPayer: Number.isFinite(netAPayer) ? netAPayer : 0,
        tvaSummary,
    };
}
